import React, { useEffect, useRef, useState } from 'react'
import {
  Button,
  Text,
  VStack,
  HStack,
  Alert,
  Box,
  DialogRoot,
  DialogBackdrop,
  DialogPositioner,
  DialogContent,
  DialogHeader,
  DialogBody,
} from '@chakra-ui/react'
import TermsOfServiceContent from './TermsOfServiceContent'

// Simple modal using standard Chakra Dialog pattern
const CustomModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: 'md' | 'lg' | 'xl';
}> = ({ isOpen, onClose, title, children, size = 'xl' }) => {
  return (
    <DialogRoot
      open={isOpen}
      onOpenChange={(e) => (e.open ? null : onClose())}
      placement="center"
      size={{ base: 'sm', md: size }}
    >
      <DialogBackdrop />
      <DialogPositioner>
        <DialogContent>
          <DialogHeader>
            <HStack justify="space-between" align="center">
              <Text fontSize="lg" fontWeight="semibold">{title}</Text>
              <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close">
                ×
              </Button>
            </HStack>
          </DialogHeader>
          <DialogBody p={0}>{children}</DialogBody>
        </DialogContent>
      </DialogPositioner>
    </DialogRoot>
  )
}

interface OAuthToSModalProps {
  isOpen: boolean
  onAccept: () => void
  onClose: () => void
  isLoading?: boolean
}

export const OAuthToSModal: React.FC<OAuthToSModalProps> = ({
  isOpen,
  onAccept,
  onClose,
  isLoading = false
}) => {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false)
  const scrollBoxRef = useRef<HTMLDivElement | null>(null)

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10 // 10px tolerance
    setHasScrolledToBottom(isAtBottom)
  }

  const handleAccept = () => {
    onAccept()
  }
  
  // If content doesn't overflow (short content), enable accept immediately
  useEffect(() => {
    const el = scrollBoxRef.current
    if (el && el.scrollHeight <= el.clientHeight) {
      setHasScrolledToBottom(true)
    }
  }, [isOpen])

  return (
    <CustomModal isOpen={isOpen} onClose={onClose} title="Welcome to LiveWello!" size="xl">
      <VStack gap={4} align="stretch" p={6}>
        <Alert.Root status="info" borderRadius="md">
          <Text fontSize="sm">
            To complete your account setup, please review and accept our Terms of Service.
          </Text>
        </Alert.Root>

        <Box
          ref={scrollBoxRef}
          overflowY="auto"
          onScroll={handleScroll}
          maxH="60vh"
          borderWidth="1px"
          borderColor="gray.200"
          borderRadius="md"
          p={4}
          bg="white"
        >
          <TermsOfServiceContent compact />
        </Box>

        <VStack gap={3} w="full">
          {!hasScrolledToBottom && (
            <Text fontSize="sm" color="gray.600" textAlign="center">
              Please scroll to read the full terms
            </Text>
          )}
          <HStack gap={3} w="full" justify="flex-end">
            <Button
              variant="ghost"
              onClick={onClose}
              size="lg"
            >
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleAccept}
              disabled={!hasScrolledToBottom}
              loading={isLoading}
              loadingText="Setting up account..."
              size="lg"
            >
              Accept and Continue
            </Button>
          </HStack>
        </VStack>
      </VStack>
    </CustomModal>
  )
}

export default OAuthToSModal
